import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { CalendarCheck2, Car, CheckCircle2, Clock, Loader2 } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";


function germanDate(value) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })
    .format(new Date(`${value}T12:00:00`));
}


export default function PublicAppointment() {
  const { token } = useParams();
  const [data, setData] = useState(undefined);
  const [error, setError] = useState("");
  const [date, setDate] = useState("");
  const [start, setStart] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/public/appointments/${token}`, { _skipAuthRedirect: true })
      .then((response) => setData(response.data))
      .catch((requestError) => { setError(apiErrorMessage(requestError)); setData(null); });
  }, [token]);

  const dates = useMemo(() => [...new Set((data?.slots || []).map((slot) => slot.local_date))], [data]);
  useEffect(() => {
    if (!date && dates.length) setDate(dates[0]);
  }, [date, dates]);
  const daySlots = (data?.slots || []).filter((slot) => slot.local_date === date);

  const book = async () => {
    if (!start) return;
    setBusy(true); setError("");
    try {
      const response = await api.post(`/public/appointments/${token}`, { start, note }, { _skipAuthRedirect: true });
      setData(response.data);
    } catch (requestError) {
      setError(apiErrorMessage(requestError));
      try {
        const refreshed = await api.get(`/public/appointments/${token}`, { _skipAuthRedirect: true });
        setData(refreshed.data); setStart("");
      } catch { /* keep the useful booking error */ }
    } finally { setBusy(false); }
  };

  if (data === undefined) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>;
  if (data === null) return <div className="flex min-h-screen items-center justify-center p-6"><Card className="max-w-lg p-8 text-center"><h1 className="text-xl font-semibold">Terminlink nicht verfügbar</h1><p className="mt-2 text-sm text-slate-500">Der Link ist ungültig oder abgelaufen. Bitte wenden Sie sich an die Werkstatt.</p></Card></div>;

  const appointmentDate = data.appointment?.start ? new Date(data.appointment.start) : null;
  return <main className="min-h-screen bg-slate-50 px-4 py-10 dark:bg-slate-950">
    <div className="mx-auto max-w-2xl">
      <div className="mb-6 text-center"><div className="text-sm font-medium text-primary">{data.company}</div><h1 className="mt-2 font-heading text-3xl font-bold">HU / TÜV-Termin</h1></div>
      <Card className="p-6">
        <div className="flex items-start gap-4 border-b pb-5"><div className="rounded-full bg-primary/10 p-3"><Car className="h-6 w-6 text-primary" /></div><div><div className="text-lg font-semibold">{data.vehicle.registration_plate}</div><div className="text-slate-500">{data.vehicle.make} {data.vehicle.model}</div>{data.vehicle.hu_due && <div className="mt-1 text-sm text-slate-500">HU fällig: {new Intl.DateTimeFormat("de-DE").format(new Date(`${data.vehicle.hu_due}T12:00:00`))}</div>}</div></div>
        {data.booked ? <div className="py-10 text-center" data-testid="appointment-booked"><CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" /><h2 className="mt-4 text-2xl font-semibold">Termin ist gebucht</h2><p className="mt-2 text-slate-500">{appointmentDate && new Intl.DateTimeFormat("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(appointmentDate)} Uhr</p><p className="mt-1 text-sm text-slate-500">Auftragsnummer: {data.appointment?.job_number}</p></div>
          : <div className="pt-6">
            <div className="mb-5"><Label htmlFor="appointment-date">Wunschtag auswählen</Label><select id="appointment-date" value={date} onChange={(event) => { setDate(event.target.value); setStart(""); }} className="mt-2 h-11 w-full rounded-md border bg-background px-3 text-sm">{dates.map((item) => <option key={item} value={item}>{germanDate(item)}</option>)}</select></div>
            <div><Label>Freie Uhrzeit</Label><div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">{daySlots.map((slot) => <Button key={slot.start} type="button" variant={start === slot.start ? "default" : "outline"} onClick={() => setStart(slot.start)}><Clock className="h-4 w-4" />{slot.local_time}</Button>)}</div></div>
            {!dates.length && <p className="rounded-md bg-amber-50 p-4 text-sm text-amber-800">Aktuell sind online keine freien Termine verfügbar. Bitte rufen Sie die Werkstatt an.</p>}
            <div className="mt-5"><Label htmlFor="appointment-note">Hinweis an die Werkstatt (optional)</Label><Textarea id="appointment-note" className="mt-2" maxLength={1000} value={note} onChange={(event) => setNote(event.target.value)} placeholder="Zum Beispiel Rückrufwunsch oder weitere Arbeiten" /></div>
            {error && <p className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700">{error === "appointment_slot_unavailable" ? "Dieser Termin wurde gerade vergeben. Bitte wählen Sie einen anderen Termin." : error}</p>}
            <Button className="mt-6 w-full" size="lg" disabled={!start || busy} onClick={book} data-testid="appointment-book-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarCheck2 className="h-4 w-4" />}Termin verbindlich buchen</Button>
            <p className="mt-3 text-center text-xs text-slate-500">Dauer ca. {data.duration_minutes} Minuten. Die Werkstatt kann Sie bei Rückfragen kontaktieren.</p>
          </div>}
      </Card>
    </div>
  </main>;
}
